import { EventEmitter, Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, IHttpConnectionOptions, HttpTransportType, LogLevel } from '@aspnet/signalr';
import { WebmsgModel } from '@shared/models/webmsg-model';
import { AppInfoService } from '@shared/services/common/app-info.service';
import { Observable } from 'rxjs';
import { HttpParams, HttpClient } from '@angular/common/http';
import { DataRefreshModel } from '@shared/models/datarefresh-model';
import { ReOpenFormModel, ReOpenMenuModel } from '@shared/models/reopenform-model';
import { OpenLoadingModel } from '@shared/models/openloading-model';
import { ChatMessageModel } from '@shared/models/chatmessage-model';
import { KanbanHubModel } from '@shared/models/kanban-hub-model';
import { ProfileHubModel } from '@shared/models/profile-hub-model';
import { JoblistChatHubModel, NotificationHubModel } from '@shared/models/notification-hub-model';
import { EventEmitterService } from '../common';
import { CallUsersModel, IceCandidateModel, OfferModel, AnswerModel, IceGroupCandidateModel, OfferGroupModel, AnswerGroupModel, ConnectionModel, ExitCallModel } from '../../models/call-users-model';
import { UserService } from './user.service';
import { PollHubVM } from '@shared/models/poll-hub-vm';
import { PollAnswersHubVM } from '@shared/models/poll-hub-answer-vm';
import { PostLikesVM, PostCommentVM, CommentLikeVM } from '@shared/models/post-like-vm';
import { ContentCreationVM, ContentDeletionVM, ContentUpdateVM } from '@shared/models/content-vm';
import { InteractionCreationVM, InteractionDeletionVM, InteractionUpdateVM } from '@shared/models/interaction-vm';
import { FollowRequestVM } from '@shared/models/follow-request-vm';

@Injectable({
    providedIn: 'root'
})
export class WebMessageService {
    messageReceived = new EventEmitter<WebmsgModel>();
    changeModule = new EventEmitter<DataRefreshModel>();
    jobListChat = new EventEmitter<JoblistChatHubModel>();
    reOpenForm = new EventEmitter<ReOpenFormModel>();
    reOpenMenu = new EventEmitter<ReOpenMenuModel>();
    chatMessageGetAll = new EventEmitter<ChatMessageModel>();
    openLoading = new EventEmitter<OpenLoadingModel>();
    projectManagementEvent = new EventEmitter<KanbanHubModel>();
    profileManagementEvent = new EventEmitter<ProfileHubModel>();
    notificationManagementEvent = new EventEmitter<NotificationHubModel>();
    chatUserStatus = new EventEmitter<number>();
    callEvent = new EventEmitter<CallUsersModel>();
    ReceiveIceCandidate = new EventEmitter<IceCandidateModel>();
    ReceiveOffer = new EventEmitter<OfferModel>();
    ReceiveAnswer = new EventEmitter<AnswerModel>();
    screenStatus = new EventEmitter<boolean>();
    sendCallingAnswer = new EventEmitter<{ answer: boolean, userId: number }>();
    endCalling = new EventEmitter<boolean>();
    exitCalling = new EventEmitter<ExitCallModel>();
    setConnectionsEvent = new EventEmitter<ConnectionModel>();
    meetingRoom = new EventEmitter<any>();
    ReceiveGroupIceCandidate = new EventEmitter<IceGroupCandidateModel>();
    ReceiveGroupOffer = new EventEmitter<OfferGroupModel>();
    ReceiveGroupAnswer = new EventEmitter<AnswerGroupModel>();
    connectionEstablished = new EventEmitter<Boolean>();
    private connectionIsEstablished = false;
    public _hubConnection: HubConnection;
    isLoggedIn = false;
    pollConfigurationEvent = new EventEmitter<PollHubVM>();
    pollAnswersConfigurationEvent = new EventEmitter<PollAnswersHubVM>();
    postLikesEvent = new EventEmitter<PostLikesVM>();
    postCommentsEvent = new EventEmitter<PostCommentVM>();
    commentLikesEvent = new EventEmitter<CommentLikeVM>();
    public userBannedEvent: EventEmitter<{ userId: number, message: string }> = new EventEmitter();

     // Content related events
     contentCreatedEvent = new EventEmitter<ContentCreationVM>();
     contentUpdatedEvent = new EventEmitter<ContentUpdateVM>();
     contentDeletedEvent = new EventEmitter<ContentDeletionVM>();
 
     // Interaction related events
     interactionCreatedEvent = new EventEmitter<InteractionCreationVM>();
     interactionUpdatedEvent = new EventEmitter<InteractionUpdateVM>();
     interactionDeletedEvent = new EventEmitter<InteractionDeletionVM>();
 
     // Follow request related events
     followRequestCreatedEvent = new EventEmitter<FollowRequestVM>();
     followRequestAcceptedEvent = new EventEmitter<FollowRequestVM>();
     followRequestRejectedEvent = new EventEmitter<FollowRequestVM>();
     followRequestCancelledEvent = new EventEmitter<FollowRequestVM>();

    constructor(
        public appInfo: AppInfoService,
        private http: HttpClient,
        public eventEmitter: EventEmitterService,
        public _userService: UserService
    ) {
    }

    sendMessage(message: WebmsgModel) {
        if (!!this._hubConnection) {
            this._hubConnection.invoke('NewWebMessage', message);
        }
    }

    saveMessageInfo(message: WebmsgModel): Observable<boolean> {
        let params = new HttpParams().set("id", message.id.toString());
        return this.http.get<boolean>(this.appInfo.appUrl + "api/generic/saveMessageInfo", { params: params });
    }


    sendMessageService(message: WebmsgModel): Observable<boolean> {
        let params = new HttpParams().set("user_id", message.user_id.toString()).set("msg_show_type", message.msg_show_type.toString()).set("message_type", message.message_type.toString())
            .set("message", message.message.toString()).set("message_title", message.message_title.toString()).set("is_read", message.is_read.toString()).set("id", message.id.toString());
        return this.http.get<boolean>(this.appInfo.appUrl + "api/generic/sendwebmessage", { params: params });
    }

    startAuthConnection() {
        if (this.isLoggedIn == false && typeof this._hubConnection == "undefined") {
            const localUserSession = localStorage.getItem("localUserSession");
            if (localUserSession) {
                const userSession = JSON.parse(localUserSession);
                const token = userSession.token;
                if (token) {
                    this.startConnection(token);
                } else {
                    console.error("JWT token bulunamadı.");
                }
            }
        }
    }

    stopConnection() {
        this.isLoggedIn = false;
        if (this._hubConnection) {
            this._hubConnection.stop();
            this._hubConnection = undefined;
        }
    }

    private createConnection(token: string) {
        const hubUrl = this.appInfo.appUrl + 'RiverHub';
        console.log('Attempting to connect to SignalR hub:', hubUrl);

        // URL'nin SSL kullanıp kullanmadığını kontrol et
        const isSecure = hubUrl.startsWith('https://');
        console.log('Connection protocol:', isSecure ? 'HTTPS (SSL)' : 'HTTP (Non-SSL)');

        const connectionOptions: IHttpConnectionOptions = {
            accessTokenFactory: () => token
        };

        // SSL durumuna göre bağlantı seçeneklerini ayarla
        if (isSecure) {
            connectionOptions.skipNegotiation = true;
            connectionOptions.transport = HttpTransportType.WebSockets;
        } else {
            // Non-SSL için daha esnek bağlantı seçenekleri
            connectionOptions.skipNegotiation = false;
            connectionOptions.transport = HttpTransportType.LongPolling;
        }

        this._hubConnection = new HubConnectionBuilder()
            .withUrl(hubUrl, connectionOptions)
            .configureLogging(LogLevel.Debug)
            .build();

        // Bağlantı durumu değişikliklerini izle
        this._hubConnection.onclose((error) => {
            console.warn('SignalR connection closed', error);
            if (this.isLoggedIn) {
                let _this = this;
                setTimeout(function () {
                    _this.startConnection(token);
                }, 5000);
            }
        });
    }

    private attemtToConnect = 0;

    private startConnection(token: string): void {
        console.log('Starting SignalR connection...');

        try {
            this.createConnection(token);
            this.isLoggedIn = true;

            this._hubConnection
                .start()
                .then(() => {
                    this.connectionIsEstablished = true;
                    this.attemtToConnect = 0;
                    console.log('SignalR connection established successfully');
                    this.connectionEstablished.emit(true);
                    this.registerOnServerEvents();
                })
                .catch(err => {
                    console.error('SignalR connection error:', err);
                    if (this.isLoggedIn) {
                        this.attemtToConnect++;
                        if (this.attemtToConnect < 3) {
                            console.log(`Retrying connection attempt ${this.attemtToConnect} of 3...`);
                            setTimeout(() => {
                                this.startConnection(token);
                            }, 5000);
                        } else {
                            console.error('Max connection attempts reached');
                            this.isLoggedIn = false;
                        }
                    }
                });
        } catch (error) {
            console.error('Error creating connection:', error);
            this.isLoggedIn = false;
        }
    }




    private registerOnServerEvents(): void {
        this.JobListChatChange();
        this.updateModule();
        this.reOpenFormEvent();
        this.reOpenMenuEvent();
        this.chatMessageGetAllEvent();
        this.openLoadingEvent();
        this.projectManagementChangeEvent();
        this.profileManagementChangeEvent();
        this.notificationManagementChangeEvent();
        this.listenForIncomingCalls();
        this.listenReceiveIceCandidate();
        this.listenReceiveOffer();
        this.listenReceiveAnswer();
        this.screenShareStatus();
        this.sendCallAnswer();
        this.endCall();
        this.exitCall();
        this.setConnections();
        this.addListeners();
        this.meetingRooms();

        // Yeni eklenen event'ler
        this.listenMessageReceived();
        this.listenMobileConfirmationResult();
        this.listenWebNotification();
        this.listenUserBanned();

        // Grup arama alanları
        this.listenReceiveGroupIceCandidate();
        this.listenReceiveGroupOffer();
        this.listenReceiveGroupAnswer();

        // Poll arama alanları
        this.listenPollConfiguration();
        this.listenPollAnswersConfiguration();

        // Post ile ilgili eventler
        this.listenPostLikes();
        this.listenPostComments();
        this.listenCommentLikes();

        // Content related listeners
        this.listenContentCreated();
        this.listenContentUpdated();
        this.listenContentDeleted();

        // Interaction related listeners
        this.listenInteractionCreated();
        this.listenInteractionUpdated();
        this.listenInteractionDeleted();

        // Follow request related listeners
        this.listenFollowRequestCreated();
        this.listenFollowRequestAccepted();
        this.listenFollowRequestRejected();
        this.listenFollowRequestCancelled();
    }

    sendChatUserId() {
        if (typeof this._hubConnection != "undefined" && this._hubConnection.state == 1) {
            this._hubConnection.invoke('ChatUserStatus', this.appInfo.currentUser.id);
        }
    }

    private updateModule(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ChangeModule', (data: any) => {
                this.changeModule.emit(data);
            });
        }
    }

    private reOpenFormEvent(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ReOpenForm', (data: any) => {
                this.reOpenForm.emit(data);
            });
        }
    }

    private reOpenMenuEvent(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ReOpenMenu', (data: any) => {
                this.reOpenMenu.emit(data);
            });
        }
    }

    private chatMessageGetAllEvent(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('SendChatMessage', (data: any) => {
                this.chatMessageGetAll.emit(data);
            });
        }

    }

    private openLoadingEvent(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('OpenLoading', (data: any) => {
                this.openLoading.emit(data);
            });
        }
    }

    private projectManagementChangeEvent(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ProjectManagementChange', (data: any) => {
                this.projectManagementEvent.emit(data);
            });
        }
    }

    private profileManagementChangeEvent(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ProfileManagementChange', (data: any) => {
                this.profileManagementEvent.emit(data);
            });
        }
    }

    private notificationManagementChangeEvent(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('NotificationManagementChange', (data: any) => {
                this.notificationManagementEvent.emit(data);
            })
        }
    }

    private JobListChatChange(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('JobListChat', (data: any) => {
                this.jobListChat.emit(data);
            })
        }
    }

    private listenForIncomingCalls(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ReceiveCall', (data: any) => {
                this.callEvent.emit(data);
            });
        }
    }

    private listenReceiveIceCandidate(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ReceiveIceCandidate', (candidate: IceCandidateModel) => {
                this.ReceiveIceCandidate.emit(candidate);
            });
        }
    }

    private listenReceiveOffer(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ReceiveOffer', (offer: OfferModel) => {
                this.ReceiveOffer.emit(offer);
            });
        }
    }

    private listenReceiveAnswer(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ReceiveAnswer', (answer: AnswerModel) => {
                this.ReceiveAnswer.emit(answer);
            });
        }
    }

    private screenShareStatus(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ScreenShareStatus', (screenStatus: boolean) => {
                this.screenStatus.emit(screenStatus);
            });
        }
    }

    private sendCallAnswer(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('sendCallAnswer', (answer: boolean, userId: number) => {
                this.sendCallingAnswer.emit({ answer: answer, userId: userId });
            });
        }
    }

    private meetingRooms(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('meetingRooms', (meeting) => {
                this.meetingRoom.emit(meeting);
            });
        }
    }

    private endCall(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('endCall', (endcall: boolean) => {
                this.endCalling.emit(endcall);
            });
        }
    }

    private exitCall(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('exitcall', (data: ExitCallModel) => {
                this.exitCalling.emit(data);
            });
        }
    }

    private setConnections(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('SetConnections', (data: any) => {
                this.setConnectionsEvent.emit(data);
            });
        }
    }

    private addListeners() {
        if (typeof this._hubConnection != "undefined") {
            // Online kullanıcılar güncellenince UserService'i bilgilendir
            this._hubConnection.on('updateOnlineUsers', (onlineUsers: string[]) => {
                this._userService.updateOnlineUsers(onlineUsers);
            });
        }
    }

    //Grup Arama Alanları
    private listenReceiveGroupIceCandidate(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ReceiveGroupIceCandidate', (candidate: IceGroupCandidateModel) => {
                this.ReceiveGroupIceCandidate.emit(candidate);
            });
        }
    }

    private listenReceiveGroupOffer(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ReceiveGroupOffer', (offer: OfferGroupModel) => {
                this.ReceiveGroupOffer.emit(offer);
            });
        }
    }

    private listenReceiveGroupAnswer(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ReceivegroupAnswer', (answer: AnswerGroupModel) => {
                this.ReceiveGroupAnswer.emit(answer);
            });
        }
    }

    private listenPollConfiguration(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ConfigurePolls', (data: PollHubVM) => {
                this.pollConfigurationEvent.emit(data);
            });
        }
    }

    private listenPollAnswersConfiguration(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ConfigurePollsAnswers', (data: PollAnswersHubVM) => {
                this.pollAnswersConfigurationEvent.emit(data);
            });
        }
    }

    private listenPostLikes(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ConfigurePostLikes', (data: PostLikesVM) => {
                this.postLikesEvent.emit(data);
            });
        }
    }

    private listenPostComments(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ConfigurePostComments', (data: PostCommentVM) => {
                this.postCommentsEvent.emit(data);
            });
        }
    }

    private listenCommentLikes(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ConfigureCommentLikes', (data: CommentLikeVM) => {
                this.commentLikesEvent.emit(data);
            });
        }
    }

    // Content related listeners
    private listenContentCreated(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ContentCreated', (data: ContentCreationVM) => {
                this.contentCreatedEvent.emit(data);
            });
        }
    }

    private listenContentUpdated(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ContentUpdated', (data: ContentUpdateVM) => {
                this.contentUpdatedEvent.emit(data);
            });
        }
    }

    private listenContentDeleted(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('ContentDeleted', (data: ContentDeletionVM) => {
                this.contentDeletedEvent.emit(data);
            });
        }

    }

    // Interaction related listeners
    private listenInteractionCreated(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('InteractionCreated', (data: InteractionCreationVM) => {
                this.interactionCreatedEvent.emit(data);
            });
        }
    }

    private listenInteractionUpdated(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('InteractionUpdated', (data: InteractionUpdateVM) => {
                this.interactionUpdatedEvent.emit(data);
            });
        }

    }

    private listenInteractionDeleted(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('InteractionDeleted', (data: InteractionDeletionVM) => {
                this.interactionDeletedEvent.emit(data);
            });
        }
    }

    // Follow request related listeners
    private listenFollowRequestCreated(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('FollowRequestCreated', (data: FollowRequestVM) => {
                this.followRequestCreatedEvent.emit(data);
            });
        }
    }

    private listenFollowRequestAccepted(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('FollowRequestAccepted', (data: FollowRequestVM) => {
                this.followRequestAcceptedEvent.emit(data);
            });
        }
    }

    private listenFollowRequestRejected(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('FollowRequestRejected', (data: FollowRequestVM) => {
                this.followRequestRejectedEvent.emit(data);
            });
        }
    }

    private listenFollowRequestCancelled(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('FollowRequestCancelled', (data: FollowRequestVM) => {
                this.followRequestCancelledEvent.emit(data);
            });
        }
    }

    // Yeni eklenen event dinleyicileri
    private listenMessageReceived(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('MessageReceived', (data: WebmsgModel) => {
                this.messageReceived.emit(data);
            });
        }
    }

    private listenMobileConfirmationResult(): void {
        // if (typeof this._hubConnection != "undefined") {
        //     this._hubConnection.on('MobileConfirmationResult', (data: any) => {
        //         // Bu event için bir EventEmitter tanımlanmalı
        //         console.log('MobileConfirmationResult received:', data);
        //     });
        // }
    }

    private listenWebNotification(): void {
        // if (typeof this._hubConnection != "undefined") {
        //     this._hubConnection.on('WebNotification', (data: any) => {
        //         // Bu event için bir EventEmitter tanımlanmalı
        //         console.log('WebNotification received:', data);
        //     });
        // }
    }

    private listenUserBanned(): void {
        if (typeof this._hubConnection != "undefined") {
            this._hubConnection.on('UserBanned', (data: { userId: number, message: string }) => {
                this.userBannedEvent.emit(data);
            });
        }
    }

}  
